import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";

export function SpelinspektionenInfoPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const title = isSv
    ? "Spelinspektionen — den svenska spelmyndigheten"
    : "Spelinspektionen — the Swedish gambling authority";
  const desc = isSv
    ? "Spelinspektionen är den svenska tillsynsmyndigheten för spel som beviljar och övervakar spellicenser."
    : "Spelinspektionen is the Swedish gambling regulator that issues and supervises gambling licenses.";

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: "Spelinspektionen" }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-8 max-w-3xl [&>h2]:mt-10 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3 [&>ul]:my-3 [&>ul]:list-disc [&>ul]:pl-6">
        <h2>{isSv ? "Vad gör Spelinspektionen?" : "What does Spelinspektionen do?"}</h2>
        <p>
          {isSv
            ? "Spelinspektionen är den svenska statliga myndighet som har ansvar för spelmarknaden i Sverige. Myndigheten utfärdar licenser, övervakar att operatörer följer regelverket, och kan utfärda varningar, sanktionsavgifter eller dra in licens om reglerna inte följs. Myndigheten driver också Spelpaus.se."
            : "Spelinspektionen is the Swedish state authority responsible for the gambling market in Sweden. It issues licenses, supervises operator compliance and can issue warnings, sanction fees or revoke licenses when rules are broken. It also operates Spelpaus.se."}
        </p>

        <h2>{isSv ? "Vilka licenser finns?" : "What licenses exist?"}</h2>
        <ul>
          <li>{isSv ? "Kommersiellt onlinespel — casino, poker, bingo." : "Commercial online gambling — casino, poker, bingo."}</li>
          <li>{isSv ? "Vadhållning — sport och esport." : "Betting — sports and esports."}</li>
          <li>{isSv ? "Spel för allmännyttiga ändamål — lotterier från ideella föreningar." : "Gambling for the public benefit — lotteries from non-profit associations."}</li>
          <li>{isSv ? "Landbaserade kasinon — Casino Cosmopol." : "Land-based casinos — Casino Cosmopol."}</li>
          <li>{isSv ? "Värdeautomater — t.ex. Vegas-spelautomater." : "Value automatons — e.g. Vegas slot machines."}</li>
        </ul>

        <h2>{isSv ? "Hur kontrollerar du licens?" : "How do you verify a license?"}</h2>
        <p>
          {isSv
            ? "Spelinspektionen publicerar en offentlig licenslista där alla giltiga licensinnehavare finns. Du kan slå upp en operatör direkt via spelinspektionen.se. Saknas operatören eller står den som ”indragen” är sajten inte längre licensierad."
            : "Spelinspektionen publishes a public license list with every active license holder. You can look up an operator directly at spelinspektionen.se. If the operator is missing or marked as ”revoked”, the site is no longer licensed."}
        </p>

        <h2>{isSv ? "Tillsyn och sanktioner" : "Supervision and sanctions"}</h2>
        <p>
          {isSv
            ? "Myndigheten genomför granskningar löpande och kan utfärda sanktioner. Sanktionerna är offentliga — listan ligger på myndighetens webbplats och uppdateras regelbundet. Allvarliga och upprepade brott mot regelverket kan leda till indragen licens."
            : "The authority conducts ongoing reviews and can issue sanctions. The sanctions are public — the list lives on the authority's website and is updated regularly. Serious or repeated violations can lead to license revocation."}
        </p>

        <h2>{isSv ? "Konsumenttvister" : "Consumer disputes"}</h2>
        <p>
          {isSv
            ? "Om du har en tvist med ett licensierat spelbolag — exempelvis om en utbetalning eller bonusvillkor — kan du i första hand kontakta operatörens kundtjänst. Får du inte rätt kan du vända dig till Allmänna reklamationsnämnden (ARN) som ger en kostnadsfri rekommendation till parterna. Operatörer med svensk licens följer normalt ARN:s rekommendation."
            : "If you have a dispute with a licensed operator — for example about a payout or bonus terms — start with the operator's customer service. If you do not get a satisfactory resolution you can take the case to ARN, the National Board for Consumer Disputes, which provides a free recommendation to both parties. Operators with a Swedish license typically follow ARN's recommendation."}
        </p>
      </article>

      <InternalLinks context="responsible" />
    </div>
  );
}
